
import React, { useContext, useState, useEffect } from 'react';
import { AppContext } from '../App';
import { BriefingField } from '../types';
import { supabase, uploadFile } from '../supabase';

const Settings: React.FC = () => {
  const context = useContext(AppContext);
  if (!context) return null;

  const { 
    company, setCompany, 
    syncAllData
  } = context;

  const [activeTab, setActiveTab] = useState<'company' | 'briefing' | 'system'>('company');
  const [logoPreview, setLogoPreview] = useState<string>(company.logoUrl || '');
  const [isSaving, setIsSaving] = useState(false);
  
  const [companyName, setCompanyName] = useState(company.name);
  const [companyCNPJ, setCompanyCNPJ] = useState(company.cnpj);

  useEffect(() => {
    if (company) {
      setCompanyName(company.name || '');
      setCompanyCNPJ(formatCNPJ(company.cnpj || ''));
      setLogoPreview(company.logoUrl || '');
    }
  }, [company]);

  const formatCNPJ = (value: string) => {
    const digits = value.replace(/\D/g, '');
    return digits
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .substring(0, 18);
  };

  const handleCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompanyCNPJ(formatCNPJ(e.target.value));
  };

  const handleUpdateCompany = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSaving(true);
    
    // Remove formatação do CNPJ para salvar apenas números no banco
    const cnpjOnlyDigits = companyCNPJ.replace(/\D/g, '');
    
    console.log('🔍 DEBUG - CNPJ antes de salvar:', {
      formatado: companyCNPJ,
      limpo: cnpjOnlyDigits,
      companyId: company.id
    });
    
    const dbPayload = {
      id: company.id || 1, 
      name: companyName,
      cnpj: cnpjOnlyDigits,
      logo_url: logoPreview
    };

    console.log('📦 Payload enviado ao Supabase:', dbPayload);

    try {
      const { data: resultData, error } = await supabase
        .from('company_info')
        .upsert(dbPayload, { onConflict: 'id' })
        .select();

      if (error) throw error;
      
      console.log('✅ Dados salvos no Supabase:', resultData);
      
      await syncAllData();
      alert('Dados da agência atualizados com sucesso!');
    } catch (err: any) {
      const errorMsg = err.message || err.details || JSON.stringify(err);
      alert('ERRO AO SALVAR:\n' + errorMsg);
      console.error('❌ Erro detalhado:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsSaving(true);
    try {
      const publicUrl = await uploadFile(file);
      setLogoPreview(publicUrl);
    } catch (err: any) {
      alert('Erro ao fazer upload: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // --- LÓGICA DO FORM BUILDER (ONBOARDING) ---
  const saveBriefingTemplate = async (updatedFields: BriefingField[]) => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('company_info')
        .upsert({ 
          id: 1, 
          briefing_template: updatedFields 
        }, { onConflict: 'id' });
      
      if (error) throw error;
      await syncAllData();
    } catch (err: any) {
      alert('Erro ao salvar formulário: ' + (err.message || JSON.stringify(err)));
    } finally {
      setIsSaving(false);
    }
  };

  const addField = () => {
    const newField: BriefingField = {
      id: `f-${Date.now()}`,
      label: 'Nova Pergunta',
      type: 'text',
      placeholder: 'Digite o exemplo de resposta...'
    };
    const updated = [...(company.briefingTemplate || []), newField];
    setCompany({ ...company, briefingTemplate: updated });
    saveBriefingTemplate(updated);
  };

  const removeField = (id: string) => {
    const updated = (company.briefingTemplate || []).filter(f => f.id !== id);
    setCompany({ ...company, briefingTemplate: updated });
    saveBriefingTemplate(updated);
  };

  const updateField = (id: string, updates: Partial<BriefingField>) => {
    const updated = (company.briefingTemplate || []).map(f => f.id === id ? { ...f, ...updates } : f);
    setCompany({ ...company, briefingTemplate: updated });
  };

  const onboardingUrl = `${window.location.origin}${window.location.pathname}#/onboarding`;

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto animate-in fade-in duration-500 pb-24 font-display">
      {/* Header conforme o print */}
      <div className="mb-6">
        <h2 className="text-3xl font-black text-text-main tracking-tight">Ajustes da Agência</h2>
        <p className="text-text-secondary font-bold text-sm mt-1">Identidade e formulários do sistema.</p>
      </div>

      {/* Card de Logout conforme o print */}
      <button 
        onClick={() => context.handleLogout()}
        className="w-full h-14 mb-8 px-6 rounded-2xl border border-primary/10 bg-white flex items-center gap-4 group hover:bg-red-50 transition-all shadow-sm"
      >
        <span className="material-symbols-outlined text-red-500 text-[20px] transition-transform group-hover:scale-110">logout</span>
        <span className="text-[10px] font-black text-red-500 uppercase tracking-[0.2em]">Sair</span>
      </button>

      {/* Seletor de Abas Corrigido (Grid 3 colunas garante simetria total) */}
      <div className="grid grid-cols-3 gap-1.5 p-1.5 bg-gray-100/60 rounded-2xl w-full mb-10">
        {[
          { id: 'company', label: 'Empresa' },
          { id: 'briefing', label: 'Onboarding' },
          { id: 'system', label: 'Geral' }
        ].map(tab => (
          <button 
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`py-3.5 rounded-xl text-[10px] font-black uppercase tracking-[0.15em] transition-all text-center ${activeTab === tab.id ? 'bg-white shadow-sm text-primary' : 'text-text-secondary hover:text-text-main'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'company' && (
        <div className="bg-white rounded-[23px] shadow-sm border border-gray-100 p-8 md:p-12 animate-in slide-in-from-bottom-4">
          <form onSubmit={handleUpdateCompany} className="flex flex-col gap-8">
            <div className="flex flex-col items-center">
              <div className="size-32 rounded-[32px] bg-white border-2 border-dashed border-primary/20 flex items-center justify-center overflow-hidden shrink-0 shadow-inner relative group">
                {logoPreview ? (
                  <img src={logoPreview} className="w-full h-full object-contain p-4" alt="Logo" />
                ) : (
                  <span className="material-symbols-outlined text-primary/30 text-5xl">hexagon</span>
                )}
                <label className="absolute inset-0 bg-primary/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  <span className="material-symbols-outlined text-white text-3xl">upload</span>
                  <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
                </label>
              </div>
              <p className="text-[9px] font-black text-text-secondary uppercase tracking-widest mt-4">Preview da Marca</p>
            </div>

            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-text-secondary ml-1">Link Direto do Logotipo</label>
                <input 
                  type="text" 
                  value={logoPreview || ''} 
                  onChange={(e) => setLogoPreview(e.target.value)}
                  className="h-14 px-6 rounded-2xl bg-gray-50 border-none outline-none text-sm font-bold transition-all focus:bg-white focus:ring-2 focus:ring-primary/10 shadow-sm"
                  placeholder="https://exemplo.com/logo.png"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-text-secondary ml-1">Nome da Agência</label>
                <input 
                  value={companyName || ''} 
                  onChange={e => setCompanyName(e.target.value)}
                  required 
                  className="h-14 px-6 rounded-2xl bg-gray-50 border-none outline-none text-sm font-bold transition-all focus:bg-white focus:ring-2 focus:ring-primary/10 shadow-sm" 
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-text-secondary ml-1">CNPJ</label>
                <input 
                  value={companyCNPJ || ''}
                  onChange={handleCNPJChange}
                  required 
                  maxLength={18}
                  className="h-14 px-6 rounded-2xl bg-gray-50 border-none outline-none text-sm font-bold transition-all focus:bg-white focus:ring-2 focus:ring-primary/10 shadow-sm" 
                />
              </div>
            </div>

            <button 
              type="submit" 
              disabled={isSaving} 
              className="h-16 rounded-2xl bg-primary text-white font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.01] transition-all disabled:opacity-50"
            >
              {isSaving ? 'SALVANDO...' : 'SALVAR IDENTIDADE'}
            </button>
          </form>
        </div>
      )}

      {activeTab === 'briefing' && (
        <div className="flex flex-col gap-8 animate-in fade-in">
          <div className="bg-primary-light/50 p-8 rounded-[23px] border border-primary/5">
             <h4 className="text-xl font-black text-primary tracking-tight">Portal de Onboarding</h4>
             <p className="text-sm font-bold text-text-secondary mt-2 mb-6">Link que você deve enviar para os novos clientes:</p>
             <div className="p-5 bg-white rounded-2xl border border-primary/10 font-mono text-[10px] text-primary break-all shadow-sm">
               {onboardingUrl}
             </div>
             <button 
              onClick={() => { navigator.clipboard.writeText(onboardingUrl); alert('Link copiado!'); }}
              className="mt-6 h-12 px-8 rounded-xl bg-primary text-white font-black uppercase text-[10px] tracking-widest shadow-lg shadow-primary/10 active:scale-95 transition-all"
             >
               Copiar Link de Acesso
             </button>
          </div>

          <div className="bg-white p-8 md:p-10 rounded-[23px] border border-gray-100 shadow-sm">
             <div className="flex items-center justify-between mb-8">
                <div>
                  <h4 className="text-xl font-black text-text-main">Builder do Formulário</h4>
                  <p className="text-[10px] font-bold text-text-secondary uppercase tracking-widest mt-1">Gerencie as perguntas do onboarding</p>
                </div>
                <button 
                  onClick={addField} 
                  className="size-12 bg-primary-light text-primary rounded-2xl flex items-center justify-center hover:bg-primary hover:text-white transition-all shadow-sm"
                >
                  <span className="material-symbols-outlined">add</span>
                </button>
             </div>

             <div className="space-y-4">
                {(company.briefingTemplate || []).map((field, idx) => (
                  <div key={field.id} className="p-6 bg-gray-50/50 rounded-3xl border border-gray-100 flex flex-col gap-4 relative group hover:bg-white transition-all">
                    <button 
                      onClick={() => removeField(field.id)} 
                      className="absolute top-6 right-6 text-gray-300 hover:text-red-500 transition-colors"
                    >
                      <span className="material-symbols-outlined text-[20px]">delete</span>
                    </button>
                    
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-2">
                        <label className="text-[9px] font-black uppercase text-text-secondary tracking-widest">Pergunta {idx + 1}</label>
                        <input 
                          value={field.label} 
                          onChange={e => updateField(field.id, { label: e.target.value })}
                          onBlur={() => saveBriefingTemplate(company.briefingTemplate || [])}
                          className="h-12 px-5 rounded-xl bg-white border border-gray-100 text-xs font-bold shadow-sm outline-none focus:ring-1 focus:ring-primary/20"
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-[9px] font-black uppercase text-text-secondary tracking-widest">Tipo do Campo</label>
                        <select 
                          value={field.type}
                          onChange={e => {
                            const newType = e.target.value as any;
                            updateField(field.id, { type: newType, options: newType === 'select' ? ['Sim', 'Não'] : undefined });
                            saveBriefingTemplate(company.briefingTemplate || []);
                          }}
                          className="h-12 px-5 rounded-xl bg-white border border-gray-100 text-[10px] font-bold uppercase tracking-widest shadow-sm outline-none"
                        >
                          <option value="text">Texto Curto</option>
                          <option value="textarea">Texto Longo</option>
                          <option value="select">Lista de Opções</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}

                {(!company.briefingTemplate || company.briefingTemplate.length === 0) && (
                  <div className="py-12 flex flex-col items-center text-center opacity-30">
                    <span className="material-symbols-outlined text-4xl">quiz</span>
                    <p className="text-xs font-bold mt-2">Nenhuma pergunta no formulário.</p>
                  </div>
                )}
             </div>
          </div>
        </div>
      )}

      {activeTab === 'system' && (
        <div className="flex flex-col gap-8 animate-in slide-in-from-right-4">
          <div className="bg-white rounded-[23px] shadow-sm border border-gray-100 p-8 md:p-12">
            <h4 className="text-xl font-black text-text-main tracking-tight mb-8">Informações de Sistema</h4>
            
            <div className="space-y-10">
              <div className="flex flex-col gap-4">
                <label className="text-[10px] font-black uppercase tracking-widest text-text-secondary ml-1">Identidade Visual Ativa</label>
                <div className="flex items-center gap-5 p-5 bg-gray-50 rounded-3xl w-fit border border-gray-100">
                  <div className="size-16 rounded-2xl bg-primary shadow-xl border-4 border-white flex items-center justify-center text-white">
                    <span className="material-symbols-outlined">palette</span>
                  </div>
                  <div>
                    <span className="text-sm font-black text-text-main block">Tema Magenta ERP</span>
                    <span className="text-[10px] font-bold text-text-secondary uppercase tracking-widest">Versão 2026.4.1</span>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-gray-50">
                 <div className="p-8 bg-primary/5 rounded-[23px] border border-primary/5">
                    <div className="flex items-center gap-3 mb-6">
                      <span className="material-symbols-outlined text-primary">cloud_done</span>
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Status do Backend</p>
                    </div>
                    <div className="space-y-4">
                       <div className="flex justify-between text-[11px] font-bold">
                          <span className="text-text-secondary">Banco de Dados</span>
                          <span className="text-green-500 flex items-center gap-1.5 font-black uppercase tracking-widest"><span className="size-1.5 bg-green-500 rounded-full animate-pulse"></span> Online</span>
                       </div>
                       <div className="flex justify-between text-[11px] font-bold">
                          <span className="text-text-secondary">API Supabase</span>
                          <span className="text-text-main">Operacional (Latência: 12ms)</span>
                       </div>
                       <div className="flex justify-between text-[11px] font-bold">
                          <span className="text-text-secondary">Deploy Region</span>
                          <span className="text-text-main uppercase">South-America (AWS)</span>
                       </div>
                    </div>
                 </div>
              </div>

              <div className="pt-8 border-t border-gray-50 flex items-center justify-between">
                <div>
                  <h5 className="text-sm font-black text-text-main">Sincronização Automática</h5>
                  <p className="text-[10px] font-bold text-text-secondary uppercase mt-1">Os dados são salvos em tempo real no Supabase</p>
                </div>
                <div className="w-14 h-7 bg-primary rounded-full relative p-1">
                   <div className="size-5 bg-white rounded-full ml-auto shadow-md"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
